<template>
	<div>
		<!-- Companies List header -->
		<a-row type="flex" :gutter="24">
			<a-col :span="12" class="mb-24">
				<a-button type="primary" @click="$router.push('/companies/create')" style="margin-right: 8px;">New Company</a-button>
				<a-button type="default" @click="showRegisterLinkPopup">Generate Register Link</a-button>
			</a-col>
			<a-col :span="12" class="mb-24 text-right">
				<!-- WebSocket connection status indicator -->
				<ConnectionStatus :show-details="true" />
			</a-col>
		</a-row>
		<!-- / Companies List header -->

		<!-- Companies List card -->
		<a-card :bordered="false" class="header-solid mb-24" :bodyStyle="{ padding: 0, paddingTop: '16px' }">
			<!-- Table search -->
			<div class="mx-25">
				<a-row type="flex" :gutter="24">
					<a-col :span="24" class="text-right">

						<a-input-search placeholder="input search text" style="max-width: 200px;" v-model="query"
							@input="debouncedSearch" />
					</a-col>
				</a-row>
			</div>
			<!-- / Table search -->

			<!-- Companies table -->
			<a-table class="mt-20" :columns="columns" :data-source="companies" rowKey="id"
				:pagination="paginationConfig"
				:loading="isNotLoading"
				@change="handleTableChange">
				<template slot="action" slot-scope="text, record">
					<a-space>
						<a-button type="primary" @click="$router.push(`/companies/edit/${record.id}`)">
							<a-icon type="edit" />
						</a-button>
						<a-button type="default" @click="showCompanyLoads(record)">
							<a-icon type="eye" />
						</a-button>
						<a-popconfirm
							:title="record.is_active ? 'Are you sure you want to deactivate this company?' : 'Are you sure you want to activate this company?'"
							ok-text="Yes"
							cancel-text="No"
							@confirm="toggleCompanyStatus(record)"
						>
							<a-button 
								:type="record.is_active ? 'danger' : 'success'" 
								:loading="togglingCompanyId === record.id"
								:title="record.is_active ? 'Deactivate' : 'Activate'"
								class="status-button"
							>
								<a-icon :type="record.is_active ? 'stop' : 'play-circle'" />
							</a-button>
						</a-popconfirm>
						<a-popconfirm
							title="Are you sure you want to delete this company? This action cannot be undone."
							ok-text="Yes"
							cancel-text="No"
							@confirm="deleteCompany(record)"
						>
							<a-button 
								type="danger" 
								:loading="deletingCompanyId === record.id"
								title="Delete Company"
							>
								<a-icon type="delete" />
							</a-button>
						</a-popconfirm>
					</a-space>
				</template>
				<!-- Created At Column -->
				<template slot="created_at" slot-scope="created_at">
					{{ formatDate(created_at) }}
				</template>
			</a-table>
			<!-- / Companies table -->

		</a-card>
		<!-- / Companies List card -->

		<!-- Company Loads Popup -->
		<CompanyLoadsPopup
			:visible="companyLoadsPopupVisible"
			:company-id="selectedCompanyId"
			:company-info="selectedCompanyInfo"
			@close="closeCompanyLoadsPopup"
		/>

		<!-- Register Link Popup -->
		<RegisterLinkPopup
			:visible="registerLinkPopupVisible"
			@close="closeRegisterLinkPopup"
		/>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import companyAPI from '../../api/company';
import ConnectionStatus from '../../components/WebSocket/ConnectionStatus.vue';
import CompanyLoadsPopup from '../../components/Companies/CompanyLoadsPopup.vue';
import RegisterLinkPopup from '../../components/Companies/RegisterLinkPopup.vue';

// Table columns
const columns = [
	{
		title: 'NAME',
		dataIndex: 'name',
		width: 200,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
	},
	{
		title: 'ADDRESS',
		dataIndex: 'address',
		width: 300,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
	},
	{
		title: 'CREATED AT',
		dataIndex: 'created_at',
		width: 200,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'created_at' },
	},
	{
		title: 'ACTION',
		dataIndex: 'action',
		scopedSlots: { customRender: 'action' },
		width: 300,
		fixed: 'right',
	},
];

export default {
	components: {
		ConnectionStatus,
		CompanyLoadsPopup,
		RegisterLinkPopup
	},
	data() {
		return {
			// Table data
			companies: [],
			columns: columns,
			loading: false,
			isNotLoading: true,

			// Pagination state
			pageSize: 10,
			currentPage: 1,
			totalCount: 0,

			// Search
			query: '',
			debouncedSearch: () => {},

			// Row selection


			// Company loads popup
			companyLoadsPopupVisible: false,
			selectedCompanyId: null,
			selectedCompanyInfo: {},

			// Register link popup state
			registerLinkPopupVisible: false,
			
			// Toggle loading state
			togglingCompanyId: null,
			
			// Delete loading state
			deletingCompanyId: null,
		}
	},
	computed: {
		// Pagination configuration with validation
		paginationConfig() {
			return {
				pageSize: this.pageSize || 10,
				current: this.currentPage || 1,
				total: this.totalCount || 0,
				onChange: this.handlePageChange,
				showSizeChanger: true,
				showQuickJumper: true,
				showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} companies`,
			};
		},

		// CSV data array
		csvData() {
			return this.companies.map(item => ({
				"ID": item.id,
				"Name": item.name,
				"Address": item.address,
				"Created At": item.created_at,
			}));
		}
	},
	methods: {
		// Company loads popup methods
		showCompanyLoads(company) {
			this.selectedCompanyId = company.id;
			this.selectedCompanyInfo = company;
			this.companyLoadsPopupVisible = true;
		},
		
		closeCompanyLoadsPopup() {
			this.companyLoadsPopupVisible = false;
			this.selectedCompanyId = null;
			this.selectedCompanyInfo = {};
		},
		
		// Handle table changes (pagination, filters, sorting)
		// Handle table changes (pagination, filters, sorting)
		async handleTableChange(pagination, filters, sorter) {
			// Update pagination
			if (pagination.current !== this.currentPage) {
				this.currentPage = pagination.current;
			}
			if (pagination.pageSize !== this.pageSize) {
				this.pageSize = pagination.pageSize;
				this.currentPage = 1; // Reset to first page when page size changes
			}

			// Update sorting
			if (sorter && sorter.columnKey) {
				this.sortBy = sorter.columnKey;
				this.sortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
			} else if (sorter && sorter.field) {
				this.sortBy = sorter.field;
				this.sortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
			} else {
				this.sortBy = null;
				this.sortOrder = null;
			}

			// Fetch data with new parameters
			await this.fetchCompanies();
		},



		async handlePageChange(page) {
			this.currentPage = page;
			await this.fetchCompanies();
		},

		// Centralized method to fetch companies with all parameters
		async fetchCompanies() {
			this.isNotLoading = true;
			
			const params = {
				page_size: this.pageSize,
				page: this.currentPage,
			};

			// Add search query if exists
			if (this.query && this.query.length >= 3) {
				params.query = this.query;
			}

			// Add sorting parameters if exists
			if (this.sortBy && this.sortOrder) {
				params.sort_by = this.sortBy;
				params.sort_order = this.sortOrder;
			}

			try {
				const response = await companyAPI.getCompanies({ params });
				this.companies = response.data || [];
				this.totalCount = response.pagination ? response.pagination.total : 0;
			} catch (error) {
				console.error('Error fetching companies:', error);
				this.companies = [];
				this.totalCount = 0;
			} finally {
				this.isNotLoading = false;
			}
		},

		// Event listener for input change on table search field.
		async onSearchChange() {
			this.currentPage = 1; // Reset to first page when searching
			await this.fetchCompanies();
		},



		// Export table in CSV format.
		csvExport(arrData) {
			let csvContent = "data:text/csv;charset=utf-8,";
			csvContent += [
				Object.keys(arrData[0]).join("|"),
				...arrData.map(item => Object.values(item).join("|"))
			]
				.join("\n")
				.replace(/(^\[)|(\]$)/gm, "");

			const data = encodeURI(csvContent);
			const link = document.createElement("a");
			link.setAttribute("href", data);
			link.setAttribute("download", "companies-export.csv");
			link.click();
		},

		// Format date for display
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
		},

		// Initialize WebSocket connection for companies
		initWebSocketCompanies() {
			// Subscribe to companies channel
			this.$wsSubscribe('companies');
			
			// Set up event listeners using global WebSocket
			this.$wsOnChannel('companies', 'new_company', this.onNewCompany);
			this.$wsOnChannel('companies', 'company_updated', this.onCompanyUpdated);
			this.$wsOnChannel('companies', 'company_deleted', this.onCompanyDeleted);
			
			// Listen to connection events
			this.$wsOn('connected', this.onWebSocketConnected);
			this.$wsOn('disconnected', this.onWebSocketDisconnected);
		},

		// WebSocket event handlers
		onWebSocketConnected(event) {
			console.log('WebSocket connected - companies subscribed');
			this.$message.success('Real-time updates connected');
		},

		onWebSocketDisconnected(event) {
			console.log('WebSocket disconnected');
			this.$message.warning('Real-time updates disconnected');
		},

		// Handle new company received via WebSocket
		onNewCompany(data) {
			console.log('New company received:', data);
			
			if (data.company) {
				// Add new company to the beginning of the list if we're on the first page
				if (this.currentPage === 1) {
					this.companies.unshift(data.company);
					
					// If we exceed the page size, remove the last item
					if (this.companies.length > this.pageSize) {
						this.companies.pop();
					}
				}
				
				// Update total count
				this.totalCount += 1;
				
				// Show notification
				this.$message.info(`New company added: ${data.company.name}`);
			}
		},

		// Handle company update received via WebSocket
		onCompanyUpdated(data) {
			console.log('Company updated:', data);
			
			if (data.company) {
				const index = this.companies.findIndex(company => company.id === data.company.id);
				if (index !== -1) {
					// Update the company in place
					this.$set(this.companies, index, data.company);
					this.$message.info(`Company updated: ${data.company.name}`);
				}
			}
		},

		// Handle company deletion received via WebSocket
		onCompanyDeleted(data) {
			console.log('Company deleted:', data);
			
			if (data.company_id) {
				const index = this.companies.findIndex(company => company.id === data.company_id);
				if (index !== -1) {
					this.companies.splice(index, 1);
					this.totalCount = Math.max(0, this.totalCount - 1);
					this.$message.info(`Company deleted: ${data.name || data.company_id}`);
				}
			}
		},

		// Clean up WebSocket listeners
		cleanupWebSocketCompanies() {
			// Unsubscribe from companies channel
			this.$wsUnsubscribe('companies');
		},

		// Register Link Popup methods
		showRegisterLinkPopup() {
			this.registerLinkPopupVisible = true;
		},

		closeRegisterLinkPopup() {
			this.registerLinkPopupVisible = false;
		},
		
		// Toggle company status (activate/deactivate)
		async toggleCompanyStatus(company) {
			this.togglingCompanyId = company.id;
			try {
				if (company.is_active) {
					await companyAPI.deactivateCompany(company.id);
					this.$message.success('Company deactivated successfully');
					// Update local data
					company.is_active = false;
				} else {
					await companyAPI.activateCompany(company.id);
					this.$message.success('Company activated successfully');
					// Update local data
					company.is_active = true;
				}
			} catch (error) {
				this.$message.error(error.response?.data?.error || 'Failed to update company status');
			} finally {
				this.togglingCompanyId = null;
			}
		},
		
		// Delete company
		async deleteCompany(company) {
			this.deletingCompanyId = company.id;
			try {
				await companyAPI.deleteCompany(company.id);
				this.$message.success('Company deleted successfully');
				// Remove company from local data
				const index = this.companies.findIndex(c => c.id === company.id);
				if (index !== -1) {
					this.companies.splice(index, 1);
					this.totalCount = Math.max(0, this.totalCount - 1);
				}
			} catch (error) {
				this.$message.error(error.response?.data?.error || 'Failed to delete company');
			} finally {
				this.deletingCompanyId = null;
			}
		}
	},
	async mounted() {
		this.debouncedSearch = debounce(this.onSearchChange, 300);
		
		// Load initial data
		await this.fetchCompanies();

		// Initialize WebSocket connection for real-time updates
		this.initWebSocketCompanies();
	},

	beforeDestroy() {
		// Clean up WebSocket subscription when component is destroyed
		this.cleanupWebSocketCompanies();
	}
}
</script>

<style lang="scss" scoped>
.table-avatar-info {
	display: flex;
	align-items: center;
}

.table-avatar-info .ant-avatar {
	margin-right: 8px;
}

// Using vuejs "Deep Selectors"
.table-avatar-info::v-deep .ant-avatar-string {
	font-size: 12px;
}

.btn-status::v-deep .anticon {
	line-height: 0;
}

.status-button .button-text {
	opacity: 0;
	transition: opacity 0.2s ease;
	margin-left: 4px;
}

.status-button:hover .button-text {
	opacity: 1;
}
</style>
